import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faImage, faImageSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

// Simple in-memory cache for image loading states
const imageCache = new Map<string, 'loading' | 'loaded' | 'error'>();
const retryCount = new Map<string, number>();
const MAX_RETRIES = 2;

type ImageProps = {
  url?: string | null;
  size?: 'fit' | '40' | '80' | '360';
  objectFit?: 'cover' | 'contain';
  fluid?: boolean;
  borders?: boolean;
  onImageLoad?: () => void;
  onImageError?: () => void;
  className?: string;
  alt?: string;
};

const Placeholder = () => (
  <div className='flex h-full w-full flex-col items-center justify-center text-gray-400'>
    <FontAwesomeIcon icon={faImage as IconProp} className='h-8 w-8' />
  </div>
);

const ErrorDisplay = () => (
  <div className='flex h-full w-full flex-col items-center justify-center text-gray-400'>
    <FontAwesomeIcon icon={faImageSlash as IconProp} className='h-8 w-8' />
  </div>
);

// Export SkeletonLoader so it can be used elsewhere
export const SkeletonLoader = () => {
  const animationStyle = `
    @keyframes shimmer {
      0% {
        background-position: -1000px 0;
      }
      100% {
        background-position: 1000px 0;
      }
    }

    .shimmer {
      animation: shimmer 2s infinite linear;
      background: linear-gradient(to right, #f6f7f8 4%, #edeef1 25%, #f6f7f8 36%);
      background-size: 1000px 100%;
    }
  `;

  return (
    <div className='shimmer relative flex h-full w-full items-center justify-center overflow-hidden rounded bg-slate-100 md:rounded-md'>
      <style>{animationStyle}</style>
    </div>
  );
};

/**
 * If url is empty, null or undefined, a placeholder will be displayed.
 * Handles image loading and error states internally with caching.
 */
export const Image = ({
  url,
  size = '80',
  fluid,
  objectFit = 'cover',
  borders = true,
  onImageLoad,
  onImageError,
  className,
  alt,
}: ImageProps) => {
  const [imageState, setImageState] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');

  useEffect(() => {
    if (!url) {
      setImageState('idle');
      return;
    }

    // Check cache first
    const cached = imageCache.get(url);
    if (cached === 'loaded') {
      setImageState('loaded');
      onImageLoad?.();
      return;
    }

    if (cached === 'error') {
      setImageState('error');
      return;
    }

    // Start loading
    setImageState('loading');

    // Create new image element for loading
    const img = new window.Image();

    const handleLoad = () => {
      imageCache.set(url, 'loaded');
      retryCount.delete(url); // Clear retry count on success
      setImageState('loaded');
      onImageLoad?.();
    };

    const handleError = () => {
      const currentRetries = retryCount.get(url) || 0;

      if (currentRetries < MAX_RETRIES) {
        // Retry loading
        const newRetryCount = currentRetries + 1;
        retryCount.set(url, newRetryCount);
        const delay = 1000 * newRetryCount;
        setTimeout(() => {
          img.src = url; // Retry
        }, delay); // Exponential backoff
      } else {
        // Max retries reached
        imageCache.set(url, 'error');
        setImageState('error');
        onImageError?.();
      }
    };

    img.onload = handleLoad;
    img.onerror = handleError;

    // Set src to start loading (without problematic CORS settings)
    img.src = url;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [url, onImageLoad, onImageError]);

  const transitionProps = {
    duration: 0.3,
  };

  return (
    <div
      className={classNames(
        className,
        'relative',
        size === '40'
          ? 'h-10 w-10'
          : size === '80'
          ? 'h-20 w-20'
          : size === 'fit'
          ? 'h-full w-full '
          : 'max-h-80 w-full',
        !borders ? 'border-0' : 'border border-gray-200',
        `flex items-center justify-center overflow-hidden rounded md:rounded-md`
      )}
      style={fluid ? { width: '100%', height: '100%' } : undefined}
    >
      <AnimatePresence>
        {!url && (
          <motion.div
            key='placeholder'
            className='absolute inset-0 h-full w-full'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transitionProps}
          >
            <Placeholder />
          </motion.div>
        )}

        {url && imageState === 'loading' && (
          <motion.div
            key='loader'
            className='absolute inset-0 h-full w-full'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transitionProps}
          >
            <SkeletonLoader />
          </motion.div>
        )}

        {url && imageState === 'loaded' && (
          <motion.img
            key='image'
            className={classNames(
              'absolute inset-0 block h-full w-full',
              objectFit === 'cover' ? 'object-cover' : 'object-contain'
            )}
            src={url}
            alt={alt || 'Image'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transitionProps}
          />
        )}

        {url && imageState === 'error' && (
          <motion.div
            key='error'
            className='absolute inset-0 h-full w-full'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transitionProps}
          >
            <ErrorDisplay />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Utility function to clear image cache (useful for development/debugging)
export const clearImageCache = () => {
  imageCache.clear();
  retryCount.clear();
};

// Debug function to inspect cache state
export const debugImageCache = () => {
  console.log('🔍 Image Cache Debug:');
  console.log('Cache entries:', Array.from(imageCache.entries()));
  console.log('Retry counts:', Array.from(retryCount.entries()));
  console.log('Total cached images:', imageCache.size);
};
